import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VRMLoaderPlugin, VRMUtils, type VRM, type VRMHumanoid } from '@pixiv/three-vrm';

export interface AvatarMeasure {
  /** 原始包围盒（模型自身单位，未归一化） */
  rawHeight: number;
  rawMin: [number, number, number];
  rawMax: [number, number, number];
  /** 归一化后（米） */
  height: number;
  /** 应用到根节点上的缩放 */
  scale: number;
  /** 源文件是不是 Z-up（需要立起来） */
  upAxis: 'Y' | 'Z';
  /** 源文件是不是 +Z 朝前（需要转 180°） */
  flipped: boolean;
  /** 绑定姿势下踝关节离脚底（模型原点）的高度，模型空间（米）。鞋底要踩在哪由它决定 */
  ankleHeight: number;
  triangles: number;
  meshes: number;
  materials: number;
  bones: number;
}

/** 手柄给的一只手的目标：位置 +（可选）朝向 */
export interface HandTarget {
  position: THREE.Vector3;
  /** 手柄的世界四元数；给了就会覆盖手骨旋转 */
  quaternion?: THREE.Quaternion | null;
}

/** 每根手指的弯曲量（0..1）。手柄没有手指关节，用扳机/抓握模拟。 */
/** 第一人称下要藏起来的部位（按材质名匹配，VRM 的材质名是建模时定的） */
const HIDE_IN_FIRST_PERSON = new Set([
  'head',
  'face',
  'hair',
  'hair_clip',
  'eyes',
  'halo',
  'mouth_inner',
  'ear',
]);

export interface FingerCurls {
  thumb: number;
  index: number;
  middle: number;
  ring: number;
  little: number;
}

/** 脚的目标位置（世界坐标）。给了就做腿部 IK，把脚钉在地面上。 */
export type FootTarget = THREE.Vector3;

/**
 * 一对「绑定姿势下测出来的朝向」（身体局部空间）。
 * 脚直接用它把脚摆平。
 */
export interface BodySpaceRotations {
  left: THREE.Quaternion | null;
  right: THREE.Quaternion | null;
}

export interface AvatarInstance {
  readonly root: THREE.Group;
  readonly vrm: VRM;
  readonly measure: AvatarMeasure;
  /** 头部骨骼（归一化骨骼，无缩放），用于后续第一人称挂载 */
  readonly headBone: THREE.Object3D | null;
  /** 这个模型实际带的表情名（VRM 预设名） */
  readonly expressions: string[];
  /** 坐 / 站两种基础姿态（腿部） */
  setPose(seated: boolean): void;
  /** 水平速度（m/s）：> 0.05 且站立时播放程序化步态 */
  setLocomotion(speed: number): void;
  /** 手柄追踪：两只手的目标（世界坐标 + 可选朝向）。给了目标的那一侧走 IK */
  setHandTargets(hands: { left: HandTarget | null; right: HandTarget | null } | null): void;
  /** 手指弯曲（手柄按键驱动；null = 恢复绑定姿势） */
  setFingerCurls(side: 'left' | 'right', curls: FingerCurls | null): void;
  /** 头骨的世界朝向（XR 下跟随头显的 yaw/pitch/roll） */
  setHeadRotation(worldQuat: THREE.Quaternion): void;
  /** 头的朝向整体翻 180°（模型绑定朝向和常规相反时用） */
  setHeadFlip(flip: boolean): void;
  /** 第一人称是否隐藏头（镜子渲染时临时取消） */
  setHeadHidden(hidden: boolean): void;
  /** 手指弯曲方向取反（不同模型手心法线朝向不同时用） */
  setFingerCurlFlip(flip: boolean): void;
  /** 肘部极向量取反 */
  setElbowFlip(flip: boolean): void;
  /** 手臂长度拉伸（FinalIK 的 armLengthMlp）：让模型的臂展能对上真人的臂展 */
  setArmStretch(side: 'left' | 'right', scale: number): void;
  /** 模型自身（未拉伸）的手臂总长（世界单位，已含 avatar 缩放） */
  readonly armLengths: { left: number; right: number };
  /** 头部俯仰（弧度，负=低头）：分摊到脊柱上，避免躯干僵直 */
  setHeadPitch(pitch: number): void;
  /** 脚部目标（世界坐标）：脚钉在地面，避免低头/下蹲时脚穿进地板 */
  setFootTargets(feet: { left: FootTarget | null; right: FootTarget | null } | null): void;
  /** 手臂伸展系数：手柄移动多少 → 模型手臂移动多少（0.6–1.4） */
  setReachScale(reach: number): void;
  /** 手腕滚转/偏转/摆向校准（弧度） */
  setWristOffset(roll: number, tilt: number, yaw: number): void;
  /** 读回某只手当前的骨骼世界位置与朝向（校准/调试用） */
  handTransform(side: 'left' | 'right'): { position: THREE.Vector3; quaternion: THREE.Quaternion } | null;
  /** 触发一个手势（目前只有 wave），duration 秒后自动结束 */
  pulseGesture(name: string, duration?: number): void;
  /** 调试：右手手心在世界空间的方向（挥手时应朝向身体正前） */
  palmNormalWorld(): THREE.Vector3 | null;
  /** 右手的骨骼位置（击掌判定用） */
  handPosition(out?: THREE.Vector3): THREE.Vector3 | null;
  /** 触发一个表情，duration 秒后自动淡回 0 */
  pulseExpression(name: string, duration?: number): void;
  /** 直接设一个表情的权重（口型用；每次覆盖，不做淡出） */
  setExpression(name: string, weight: number): void;
  update(dt: number): void;
  dispose(): void;
}

export interface LoadOptions {  /** 目标身高（米）。0 表示不归一化 */
  targetHeight?: number;
  /** 是否开启春季骨骼（头发/裙摆物理） */
  springBones?: boolean;
}

/**
 * 加载 VRM/glb 头像。
 *
 * 做的四件事：
 *  1. `VRMLoaderPlugin` 解析 VRM 扩展（humanoid 映射 / MToon 材质 / 表情 / 弹簧骨）；
 *  2. 清掉无用顶点、合并骨架 —— 这个模型有 16 个 skin、209 根骨，不合并开销很大；
 *  3. 按包围盒归一化到目标身高，并把脚放到 y = 0（模型原始单位五花八门）；
 *  4. 面朝 -Z（VRM 1.0 已经是这个约定；0.x 由 `rotateVRM0` 修正）。
 */
/**
 * 用 humanoid 的 hips→head 方向判断「上」轴。
 * 文件名/扩展名都不可信：VRM 规范说 Y-up，但实际文件常是 Blender 的 Z-up 直出。
 */
function detectUpAxis(vrm: VRM): 'Y' | 'Z' {
  const hips = vrm.humanoid?.getNormalizedBoneNode('hips');
  const head = vrm.humanoid?.getNormalizedBoneNode('head');
  if (!hips || !head) return 'Y';

  vrm.scene.updateMatrixWorld(true);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  hips.getWorldPosition(a);
  head.getWorldPosition(b);
  const delta = b.sub(a);
  return Math.abs(delta.z) > Math.abs(delta.y) ? 'Z' : 'Y';
}

/**
 * 用左右脚的位置判断「前」是不是 +Z（glTF 约定是 -Z）。
 * 右手系、Y-up：forward = up × right。
 */
function detectFacingFlip(vrm: VRM): boolean {
  const hum = vrm.humanoid;
  const lf = hum?.getNormalizedBoneNode('leftFoot');
  const rf = hum?.getNormalizedBoneNode('rightFoot');
  if (!lf || !rf) return false;

  vrm.scene.updateMatrixWorld(true);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  lf.getWorldPosition(a);
  rf.getWorldPosition(b);

  const right = b.sub(a);
  right.y = 0;
  if (right.lengthSq() < 1e-8) return false;
  right.normalize();

  const forward = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), right).normalize();
  return forward.z > 0;
}

/** 把一根骨头转过去，让它指向世界空间的某个方向（与骨骼自身轴向无关，所以对任何 rig 都成立） */
function aimBone(bone: THREE.Object3D, child: THREE.Object3D, dir: THREE.Vector3): void {
  bone.updateWorldMatrix(true, false);
  child.updateWorldMatrix(true, false);

  const head = bone.getWorldPosition(new THREE.Vector3());
  const tail = child.getWorldPosition(new THREE.Vector3());
  const from = tail.sub(head);
  if (from.lengthSq() < 1e-10) return;
  from.normalize();

  const worldQ = new THREE.Quaternion().setFromUnitVectors(from, dir.clone().normalize());
  const parent = bone.parent;
  if (!parent) return;

  // 世界空间的旋转换算到骨骼局部空间
  const pq = parent.getWorldQuaternion(new THREE.Quaternion());
  const localQ = pq.clone().invert().multiply(worldQ).multiply(pq);
  bone.quaternion.premultiply(localQ);
  bone.updateWorldMatrix(false, true);
}

/* 两骨手臂 IK 的临时量 */
const ikShoulder = new THREE.Vector3();
const ikElbowNow = new THREE.Vector3();
const ikHandNow = new THREE.Vector3();
const ikDir = new THREE.Vector3();
const ikPole = new THREE.Vector3();
const ikPerp = new THREE.Vector3();
const ikElbow = new THREE.Vector3();
const ikReach = new THREE.Vector3();

/**
 * 把 `pole` 投影到「垂直于 `dir`」的平面上，得到中间关节（肘/膝）该往哪边弯的**单位**方向。
 *
 * 极向量和骨骼方向平行时投影退化成 0（例如手举到正上方、极向量又是竖直向下），
 * 这时按 世界Y → 世界X 依次兜底，保证任何姿势都还能算出一个方向，
 * 而不是把 NaN 写进骨骼四元数里（那会让整条链消失）。
 */
function perpendicularInto(pole: THREE.Vector3, dir: THREE.Vector3, out: THREE.Vector3): void {
  out.copy(pole).addScaledVector(dir, -pole.dot(dir));
  if (out.lengthSq() > 1e-8) {
    out.normalize();
    return;
  }
  out.set(0, 1, 0).addScaledVector(dir, -dir.y);
  if (out.lengthSq() > 1e-8) {
    out.normalize();
    return;
  }
  out.set(1, 0, 0).addScaledVector(dir, -dir.x).normalize();
}

/**
 * 手柄姿态 → 手骨姿态的**校准四元数**（每只手各算一次）。
 *
 * 不能假设"手骨 +Y 就是手指方向"——VRM 骨骼轴向随模型而定。
 * 所以加载时从骨骼本身测两个轴：
 *   - 手指方向：中指近节相对手掌的方向
 *   - 手心法线：**(指根连线) × (手指方向)**，几何上恒成立，不依赖"猜哪个轴"
 *     绑定姿势（T-pose）下手心朝上，所以这个叉乘直接给出掌心朝外的那一侧。
 * 再用这两个轴构造基，算出「手骨局部系 → 手柄局部系」的旋转：
 *   手指 → 手柄 −Z（指向）  ；手心 → 朝向另一只手（水平左右）
 */
function computeHandOffset(
  humanoid: VRMHumanoid | null,
  hand: THREE.Object3D,
  side: 'left' | 'right',
  palmLocalFallback: THREE.Vector3 | null,
): THREE.Quaternion {
  hand.updateWorldMatrix(true, false);
  const handQ = hand.getWorldQuaternion(new THREE.Quaternion()).invert();
  const handPos = hand.getWorldPosition(new THREE.Vector3());

  const bone = (name: string): THREE.Object3D | null =>
    humanoid?.getNormalizedBoneNode(name as never) ?? null;
  const prefix = side === 'left' ? 'left' : 'right';

  /** 取某根骨相对手掌的方向（换算到手的局部空间） */
  const dirTo = (obj: THREE.Object3D | null): THREE.Vector3 | null => {
    if (!obj) return null;
    obj.updateWorldMatrix(true, false);
    const v = obj.getWorldPosition(new THREE.Vector3()).sub(handPos);
    if (v.lengthSq() < 1e-10) return null;
    return v.normalize().applyQuaternion(handQ);
  };

  const middle = dirTo(bone(`${prefix}MiddleProximal`));
  const index = dirTo(bone(`${prefix}IndexProximal`));
  const little = dirTo(bone(`${prefix}LittleProximal`));

  // 手指方向：优先中指，退化到手骨 +Y
  const finger = (middle ?? new THREE.Vector3(0, 1, 0)).clone().normalize();

  // 手心法线：指根连线 × 手指方向
  let palm: THREE.Vector3 | null = null;
  if (index && little) {
    const knuckle = little.clone().sub(index).normalize();
    const n = new THREE.Vector3().crossVectors(knuckle, finger).normalize();
    if (n.lengthSq() > 1e-6) palm = n;
  }
  if (!palm) palm = (palmLocalFallback ?? new THREE.Vector3(0, 0, 1)).clone();
  // 正交化
  palm.addScaledVector(finger, -palm.dot(finger));
  if (palm.lengthSq() < 1e-6) palm.set(0, 0, 1).addScaledVector(finger, -finger.z);
  palm.normalize();

  const mLocal = new THREE.Matrix4().makeBasis(
    finger,
    palm,
    new THREE.Vector3().crossVectors(finger, palm).normalize(),
  );

  // 手柄局部系里的目标：手指沿 −Z；手心**朝另一只手**（水平的左右方向）。
  // 自然握持时手背朝外、手心朝内：右手 → 手柄 −X，左手 → 手柄 +X。
  const tFinger = new THREE.Vector3(0, 0, -1);
  const tPalm = new THREE.Vector3(side === 'left' ? 1 : -1, 0, 0);
  const mTarget = new THREE.Matrix4().makeBasis(
    tFinger,
    tPalm,
    new THREE.Vector3().crossVectors(tFinger, tPalm).normalize(),
  );

  const m = new THREE.Matrix4().multiplyMatrices(mTarget, mLocal.transpose());
  return new THREE.Quaternion().setFromRotationMatrix(m);
}

/** 把骨骼的世界朝向设成给定四元数（换算到父空间） */
function setBoneWorldQuaternion(bone: THREE.Object3D, worldQ: THREE.Quaternion): void {
  const parent = bone.parent;
  if (!parent) return;
  parent.updateWorldMatrix(true, false);
  const pq = parent.getWorldQuaternion(new THREE.Quaternion()).invert();
  bone.quaternion.copy(pq.multiply(worldQ));
  bone.updateWorldMatrix(false, true);
}

/**
 * 两骨 IK 通用实现：把一个「上段→中段→末端」的链摆到让末端够到 target。
 * pole 决定中间关节朝哪边弯（手臂朝下、膝盖朝前）。
 */
function solveLimbIK(
  upper: THREE.Object3D,
  middle: THREE.Object3D,
  end: THREE.Object3D,
  target: THREE.Vector3,
  pole: THREE.Vector3,
): void {
  upper.updateWorldMatrix(true, false);
  upper.getWorldPosition(ikShoulder);
  middle.getWorldPosition(ikElbowNow);
  end.getWorldPosition(ikHandNow);

  const l1 = ikShoulder.distanceTo(ikElbowNow);
  const l2 = ikElbowNow.distanceTo(ikHandNow);
  if (l1 < 1e-4 || l2 < 1e-4) return;

  ikDir.copy(target).sub(ikShoulder);
  const dist = ikDir.length();
  if (dist < 1e-4) return;
  ikDir.divideScalar(dist);
  const d = Math.min(l1 + l2 - 0.01, Math.max(Math.abs(l1 - l2) + 0.01, dist));

  perpendicularInto(pole, ikDir, ikPerp);

  const x = (l1 * l1 - l2 * l2 + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, l1 * l1 - x * x));
  ikElbow.copy(ikShoulder).addScaledVector(ikDir, x).addScaledVector(ikPerp, h);

  aimBone(upper, middle, ikElbow.sub(ikShoulder));

  middle.updateWorldMatrix(true, false);
  middle.getWorldPosition(ikElbowNow);
  ikReach.copy(ikDir).multiplyScalar(d).add(ikShoulder);
  aimBone(middle, end, ikReach.sub(ikElbowNow));
}

/**
 * 两骨 IK：把「肩→肘→手」摆到让手尽量够到 `target`（世界坐标）。
 *
 * 肘朝哪边弯 = **弯曲平面**，这是 VR 手臂 IK 唯一真正重要的自由度：
 * 余弦定理有两个解，另一个解就是"胳膊肘朝前/朝上翻"。这里按 FinalIK VRIK 的思路用极向量：
 *
 *  - `elbowPole` 由调用方给，是**身体坐标**下的「外 + 下 + 后」（VRIK 的 `bendDirection`
 *    默认值就是 `Vector3.back`，配合根节点下的 bendGoal），再由身体四元数转到世界。
 *    这样船在晃、身体在转时肘始终朝着身体的下后方，不会翻到前面或朝天。
 *  - 左右必须各自朝外：之前两侧都用 +X（角色右），左肘被推向身体中线，
 *    镜像姿势下左右肘不对称（实测偏移方向差 0.215）。
 *  - `elbowFlip` 直接**取反整个极向量**，把肘翻到肩手连线另一侧。
 *    原来只翻 Z 分量，而"手往前伸"时 Z 几乎与手臂平行，投影时被减掉——
 *    实测肘位置只动 0.000 m，这就是"勾不勾没区别"的原因。
 *
 * 为什么不用「手骨朝向」推导极向量（VRChat 那种转手腕肘跟着转）：试过，
 * 手掌朝向一换（比如手心朝上握着）算出来的极向量会指到肩手连线**上方**
 * （实测 perpDir.y = +0.74），等于又造出"肘反了"。所以极向量只取身体坐标，
 * 手腕朝向只用来决定手骨自身的旋转。
 *
 * 目标超出臂展时按比例收，不会出现"手臂被拉长"。
 */
function poseArmIK(
  upperArm: THREE.Object3D,
  lowerArm: THREE.Object3D,
  hand: THREE.Object3D,
  target: HandTarget,
  reach: number,
  wristOffset: number,
  tiltOffset: number,
  yawOffset: number,
  side: 'left' | 'right',
  handOffsetQ: THREE.Quaternion | null,
  elbowPole: THREE.Vector3,
  elbowFlip: boolean,
): void {
  upperArm.updateWorldMatrix(true, false);
  upperArm.getWorldPosition(ikShoulder);
  lowerArm.getWorldPosition(ikElbowNow);
  hand.getWorldPosition(ikHandNow);

  const l1 = ikShoulder.distanceTo(ikElbowNow);
  const l2 = ikElbowNow.distanceTo(ikHandNow);
  if (l1 < 1e-4 || l2 < 1e-4) return;

  ikDir.copy(target.position).sub(ikShoulder);
  const rawDist = ikDir.length();
  if (rawDist < 1e-4) return;
  ikDir.divideScalar(rawDist);

  // reach：手柄移动多少，手臂就移动多少。1 = 1:1；< 1 让动作更省力，> 1 更夸张
  const wanted = rawDist * reach;
  const d = Math.min(l1 + l2 - 0.01, Math.max(Math.abs(l1 - l2) + 0.01, wanted));

  ikPole.copy(elbowPole);
  if (elbowFlip) ikPole.negate();

  // 手腕滚转参与决定肘平面（真实人体里肘的位置受肩内外旋 + 手腕朝向约束）。
  // 但不能全信手腕：某些握持角算出来的极向量会指到肩手连线上方，看起来就是"肘反了"。
  // 所以只把它当**提示**——相对默认极向量的偏转角限制在 ±45°、权重 0.6。
  if (target.quaternion) {
    // 手心在控制器局部系里是 ±X（computeHandOffset 就是这么映射的），肘默认在反侧
    const hint = new THREE.Vector3(side === 'left' ? -1 : 1, 0, 0)
      .applyQuaternion(target.quaternion)
      .normalize();

    const base = ikPole.clone();
    // 两者都投到"垂直于肩→手方向"的平面上
    base.addScaledVector(ikDir, -base.dot(ikDir));
    hint.addScaledVector(ikDir, -hint.dot(ikDir));

    if (base.lengthSq() > 1e-6 && hint.lengthSq() > 1e-6) {
      base.normalize();
      hint.normalize();
      const cross = new THREE.Vector3().crossVectors(base, hint);
      const signed = Math.atan2(cross.dot(ikDir), base.dot(hint));
      const maxSwivel = Math.PI / 4;
      const swivel = Math.max(-maxSwivel, Math.min(maxSwivel, signed * 0.6));
      ikPole.copy(base).applyAxisAngle(ikDir, swivel);
    }
  }

  perpendicularInto(ikPole, ikDir, ikPerp);

  const x = (l1 * l1 - l2 * l2 + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, l1 * l1 - x * x));
  ikElbow.copy(ikShoulder).addScaledVector(ikDir, x).addScaledVector(ikPerp, h);

  aimBone(upperArm, lowerArm, ikElbow.sub(ikShoulder));

  lowerArm.updateWorldMatrix(true, false);
  lowerArm.getWorldPosition(ikElbowNow);
  // 手要够到 target 在 reach 缩放后的位置（而不是原始手柄位置）
  ikElbow.copy(ikDir).multiplyScalar(d).add(ikShoulder);
  aimBone(lowerArm, hand, ikElbow.sub(ikElbowNow));

  // 朝向：手柄四元数 × 每只手的校准四元数，再叠三个世界空间的手腕自由度
  //   滚转（绕手指方向）/ 偏转（绕手掌左右轴）/ 摆向（绕手心法线）
  if (target.quaternion) {
    const base = new THREE.Quaternion().copy(target.quaternion);
    if (handOffsetQ) base.multiply(handOffsetQ);

    const rot = new THREE.Quaternion();
    const axis = new THREE.Vector3();

    if (Math.abs(wristOffset) > 1e-4) {
      // 绕手指方向滚：手心朝另一只手翻
      axis.set(0, 0, -1).applyQuaternion(base).normalize();
      rot.premultiply(new THREE.Quaternion().setFromAxisAngle(axis, wristOffset));
    }
    if (Math.abs(tiltOffset) > 1e-4) {
      // 绕手掌左右轴偏转（手腕上下/侧向掰）
      axis.set(0, 1, 0).applyQuaternion(base).applyQuaternion(rot).normalize();
      rot.premultiply(new THREE.Quaternion().setFromAxisAngle(axis, tiltOffset));
    }
    if (Math.abs(yawOffset) > 1e-4) {
      // 绕手心法线摆（左右摆腕）
      axis
        .set(side === 'left' ? 1 : -1, 0, 0)
        .applyQuaternion(base)
        .applyQuaternion(rot)
        .normalize();
      rot.premultiply(new THREE.Quaternion().setFromAxisAngle(axis, yawOffset));
    }

    setBoneWorldQuaternion(hand, rot.multiply(base));
  }
}


const ARM_REST = {
  upper: { x: 0.46, y: -1, z: 0.02 },
  lower: { x: 0.34, y: -1, z: -0.12 },
} as const;

/**
 * 左右两侧的「向外」符号。
 *
 * 这个模型经过朝向修正（原本 +Z 朝前，被转了 180°）后，角色局部 **+X 指向角色的右侧**。
 * 所以「左臂向外」= −X，「右臂向外」= +X。
 * 之前把两边都写反了，结果两条手臂是往身体中间夹的——看起来就是"垂得很低、贴着腿"。
 * 用常量固定下来，避免以后又调反。
 */
const OUT_L = -1;
const OUT_R = 1;

/**
 * 一个「放松站立」的基础姿势。
 * 绑 T-pose 的模型直接摆到场景里像个稻草人，先把它放下来——这一步不依赖任何动画资源。
 *
 * 方向同样写成**身体局部方向**（+Y 上、面朝 -Z），再用根节点世界四元数转到世界空间：
 * 这样即使以后在 root 已旋转/倾斜时重新摆姿势，也不会退化成"手臂钉在世界坐标里"。
 */
function applyRelaxedPose(vrm: VRM, root: THREE.Object3D, footFrames?: BodySpaceRotations | null): void {
  poseBody(vrm, root, {
    seated: false,
    moving: false,
    swing: 0,
    wave: 0,
    palm: 0,
    palmLocal: null,
    reach: 1,
    wristOffset: 0,
    tiltOffset: 0,
    yawOffset: 0,
    elbowFlip: false,
    headPitch: 0,
    handOffsets: null,
    footFrames: footFrames ?? null,
  });
}

/* 姿态计算用的临时量：每帧复用，避免在帧循环里分配 */
const poseBodyQ = new THREE.Quaternion();
const poseDir = new THREE.Vector3();
const poseV1 = new THREE.Vector3();
const poseV2 = new THREE.Vector3();
const palmQ = new THREE.Quaternion();
const palmV = new THREE.Vector3();
const palmAxis = new THREE.Vector3();
const palmProjected = new THREE.Vector3();
const palmTarget = new THREE.Vector3();
const palmCross = new THREE.Vector3();
/** 手臂 IK 的极向量（身体坐标的「外+下+后」，左右共用一份，用前按边设符号） */
const elbowPoleBody = new THREE.Vector3();
/** 脚踝朝向用 */
const stanceFootQ = new THREE.Quaternion();
const stanceYawQ = new THREE.Quaternion();
const bodyUpAxis = new THREE.Vector3();

/**
 * 脚尖外八角度（弧度）：真人站姿脚尖略朝外，也给"内八"上个保险。
 * 设成 0 就是完全照模型绑定姿势的脚朝向。
 */
const FOOT_STANCE_YAW = (6 * Math.PI) / 180;

/**
 * 把两只脚摆回「绑定姿势的朝向」，而不是 aim 脚尖。
 *
 * 为什么不再 `aimBone(foot, toes, 正前略下)`：
 *  - 它只约束「踝→脚尖」这一根方向，脚的**滚转**交给 `setFromUnitVectors` 的最小旋转，
 *    于是脚掌会绕这条轴翻边（鞋底朝天/侧躺），看起来像"脚被拧了一下"；
 *  - 把脚尖强行压到 14° 俯角，等于让人一直踮着脚站，鞋尖还会扎进甲板；
 *  - 它是在 IK 之后直接覆盖链子末端，腿上一有扭转就原样放大到脚上。
 *
 * 正确做法（和 VRIK / VRChat 一致）：脚骨的世界朝向 = 身体朝向 × 绑定姿势下脚的朝向。
 * 关节位置仍由腿部 IK 决定，这里**只改旋转**，所以不会破坏 IK 链；
 * 脚掌永远水平、脚尖朝前，再叠一点外八。
 */
function poseStanceFeet(
  left: THREE.Object3D | null,
  right: THREE.Object3D | null,
  frames: BodySpaceRotations | null,
  bodyQ: THREE.Quaternion,
): void {
  if (!frames) return;
  bodyUpAxis.set(0, 1, 0).applyQuaternion(bodyQ).normalize();

  if (left && frames.left) {
    stanceFootQ.copy(bodyQ).multiply(frames.left);
    stanceFootQ.premultiply(stanceYawQ.setFromAxisAngle(bodyUpAxis, FOOT_STANCE_YAW));
    setBoneWorldQuaternion(left, stanceFootQ);
  }
  if (right && frames.right) {
    stanceFootQ.copy(bodyQ).multiply(frames.right);
    stanceFootQ.premultiply(stanceYawQ.setFromAxisAngle(bodyUpAxis, -FOOT_STANCE_YAW));
    setBoneWorldQuaternion(right, stanceFootQ);
  }
}

/**
 * 挥手时让手心朝向"身体正前方"（也就是对面那个人）。
 *
 * 不猜角度：加载时先在**绑定姿势**（T-pose，手心朝下）里找出"手心法线"对应哪个局部轴，
 * 挥手时把该轴当前世界方向投影到垂直于手臂的平面上，再与目标方向比对，
 * 直接算出需要绕手臂轴滚多少度。换模型也不会错。
 */
function alignPalmToForward(
  lowerArm: THREE.Object3D,
  hand: THREE.Object3D,
  palmLocal: THREE.Vector3,
  bodyYaw: number,
): void {
  lowerArm.getWorldPosition(poseV1);
  hand.getWorldPosition(poseV2);
  palmAxis.copy(poseV2).sub(poseV1);
  if (palmAxis.lengthSq() < 1e-8) return;
  palmAxis.normalize();

  hand.getWorldQuaternion(palmQ);
  palmV.copy(palmLocal).applyQuaternion(palmQ);
  palmProjected.copy(palmV).addScaledVector(palmAxis, -palmV.dot(palmAxis));
  if (palmProjected.lengthSq() < 1e-6) return;
  palmProjected.normalize();

  // 目标：身体正前（−Z 绕 yaw）
  palmTarget.set(-Math.sin(bodyYaw), 0, -Math.cos(bodyYaw));
  palmTarget.addScaledVector(palmAxis, -palmTarget.dot(palmAxis));
  if (palmTarget.lengthSq() < 1e-6) return;
  palmTarget.normalize();

  const sin = palmCross.crossVectors(palmProjected, palmTarget).dot(palmAxis);
  const cos = palmProjected.dot(palmTarget);
  rollBone(hand, palmAxis, Math.atan2(sin, cos));
}

/**
 * 手心法线相对"绑定姿势朝下那个轴"的符号。
 *
 * 标准 T-pose 手心朝下，那个轴就是手心；但这个模型导出时手心朝上，
 * 检测到的其实是**手背**法线，于是挥手时手心正好反向 180°。
 * 换模型如果又反了，把这个值取反即可（两处读数都已用它，改一个地方就够）。
 */
const PALM_NORMAL_SIGN = -1;

/** 从绑定姿势推断"手心法线"是骨骼局部空间的哪个轴（T-pose 下手心朝下） */
function detectPalmLocal(hand: THREE.Object3D): THREE.Vector3 {
  hand.updateWorldMatrix(true, false);
  hand.getWorldQuaternion(palmQ);

  let best = new THREE.Vector3(0, 0, 1);
  let bestScore = -Infinity;
  for (const [x, y, z] of [
    [0, 0, 1],
    [0, 0, -1],
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
  ] as const) {
    palmV.set(x, y, z).applyQuaternion(palmQ);
    const score = -palmV.y; // 世界方向越朝下越像"手心"
    if (score > bestScore) {
      bestScore = score;
      best.set(x, y, z);
    }
  }
  return best.multiplyScalar(PALM_NORMAL_SIGN);
}

export interface PoseOptions {
  seated: boolean;
  /** 走路的摆动相位 (−1..1)，0 = 站定 */
  swing: number;
  moving: boolean;
  /** 挥手的进度（秒）；<= 0 表示没在挥手 */
  wave: number;
  /** 举手（击掌）的进度（秒）；<= 0 表示没举 */
  palm: number;
  /** 右手"手心法线"在骨骼局部空间的轴（加载时从绑定姿势测出） */
  palmLocal: THREE.Vector3 | null;
  /** 手柄追踪：两只手的目标（世界坐标 + 可选朝向）。给了目标的那一侧走 IK */
  hands?: { left: HandTarget | null; right: HandTarget | null } | null;
  /** 脚的目标（世界坐标）。给了就把脚钉在那里，膝盖弯曲来吸收身体升降 */
  feet?: { left: FootTarget | null; right: FootTarget | null } | null;
  /** 手臂伸展系数（1 = 1:1） */
  reach: number;
  /** 腕部滚转校准（弧度，绕手指方向） */
  wristOffset: number;
  /** 腕部偏转校准（弧度，绕手掌左右轴） */
  tiltOffset: number;
  /** 腕部摆向校准（弧度，绕手心法线） */
  yawOffset: number;
  /** 肘部极向量取反（模型左右手朝向不一时用） */
  elbowFlip: boolean;
  /** 头部俯仰（弧度）：分摊到脊柱 */
  headPitch: number;
  /** 左/右手柄 → 手骨的校准旋转 */
  handOffsets?: { left: THREE.Quaternion | null; right: THREE.Quaternion | null } | null;
  /** 绑定姿势下的脚骨朝向（身体空间）：用来把脚掌摆平，而不是 aim 脚尖 */
  footFrames?: BodySpaceRotations | null;
}

/**
 * 挥手的「手腕滚转」。
 *
 * `aimBone` 只控制骨骼**指向**（肩→肘、肘→腕），绕这根轴的滚转是自由的，
 * 于是手心朝向完全取决于绑定姿势——挥手时就会看到手掌朝天/朝后。
 * 这里绕前臂轴把腕骨滚一下，只改手心朝向、不改手臂指向。
 * 角度是调出来的常量：如果手心还是反的，把它取反。
 */
const WAVE_HAND_TWIST = -1.15;

/** 绕世界轴给骨骼加滚转（不改变骨骼指向） */
function rollBone(bone: THREE.Object3D, axis: THREE.Vector3, angle: number): void {
  const parent = bone.parent;
  if (!parent) return;
  parent.updateWorldMatrix(true, false);

  const parentQ = parent.getWorldQuaternion(new THREE.Quaternion());
  const inv = parentQ.clone().invert();
  const worldQ = new THREE.Quaternion().setFromAxisAngle(axis, angle);

  bone.quaternion.premultiply(inv.multiply(worldQ).multiply(parentQ));
  bone.updateWorldMatrix(false, true);
}
function poseBody(vrm: VRM, root: THREE.Object3D, opts: PoseOptions): void {
  const hum = vrm.humanoid;
  if (!hum) return;
  const b = (name: Parameters<typeof hum.getNormalizedBoneNode>[0]) =>
    hum.getNormalizedBoneNode(name) ?? null;

  const lUpperArm = b('leftUpperArm');
  const lLowerArm = b('leftLowerArm');
  const lHand = b('leftHand');
  const rUpperArm = b('rightUpperArm');
  const rLowerArm = b('rightLowerArm');
  const rHand = b('rightHand');
  const lUpperLeg = b('leftUpperLeg');
  const lLowerLeg = b('leftLowerLeg');
  const lFoot = b('leftFoot');
  const rUpperLeg = b('rightUpperLeg');
  const rLowerLeg = b('rightLowerLeg');
  const rFoot = b('rightFoot');

  root.updateWorldMatrix(true, true);
  root.getWorldQuaternion(poseBodyQ);

  const aim = (
    bone: THREE.Object3D | null,
    child: THREE.Object3D | null,
    x: number,
    y: number,
    z: number,
  ): void => {
    if (!bone || !child) return;
    aimBone(bone, child, poseDir.set(x, y, z).applyQuaternion(poseBodyQ));
  };

  /* ---- 脊柱：把头部俯仰分摊下去，躯干才不僵（VRChat 的两段脊柱解算的简化版） ---- */
  //
  // 顺序很重要：肩关节挂在 upperChest 下面，脊柱一转肩膀就跟着移位。
  // 先做手臂 IK 再转脊柱，等于把刚解好的手又拽离目标——实测头低 34° 时手会偏 9 cm，
  // 手臂看上去"够不到手柄"。所以脊柱必须先摆。
  {
    // 基准方向取「身体的上方」，不是世界正上方：船在晃的时候脊柱得跟着身体
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(poseBodyQ).normalize();
    const upLocal = new THREE.Vector3(0, 1, 0).applyQuaternion(poseBodyQ).normalize();
    const tilt = (k: number): THREE.Vector3 => upLocal.clone().applyAxisAngle(right, opts.headPitch * k);
    const spineB = b('spine');
    const chestB = b('chest');
    const upperChestB = b('upperChest');
    const neckB = b('neck');
    if (spineB && chestB) aimBone(spineB, chestB, tilt(0.35));
    if (chestB && upperChestB) aimBone(chestB, upperChestB, tilt(0.7));
    else if (chestB && neckB) aimBone(chestB, neckB, tilt(0.8));
    if (upperChestB && neckB) aimBone(upperChestB, neckB, tilt(1.0));
  }

  /* ---- 手臂 ---- */
  const armSwing = opts.moving ? opts.swing * 0.3 : 0;
  const leftTarget = opts.hands?.left ?? null;
  const rightTarget = opts.hands?.right ?? null;

  // 左臂：有手柄目标就 IK，否则程序化下垂/反摆
  if (leftTarget && lUpperArm && lLowerArm && lHand) {
    poseArmIK(
      lUpperArm,
      lLowerArm,
      lHand,
      leftTarget,
      opts.reach,
      // 左手三个自由度全部取反：左手校准四元数是右手的镜像，
      // 同一角度在镜像坐标系里会转成相反方向，不取反就是"左右手反向"。
      -opts.wristOffset,
      -opts.tiltOffset,
      -opts.yawOffset,
      'left',
      opts.handOffsets?.left ?? null,
      // 身体坐标下的「外 + 下 + 后」，左臂朝 −X
      elbowPoleBody.set(OUT_L * 0.25, -1, 0.55).normalize().applyQuaternion(poseBodyQ),
      opts.elbowFlip,
    );
  } else {
    aim(lUpperArm, lLowerArm, OUT_L * ARM_REST.upper.x, -1, ARM_REST.upper.z + armSwing);
    aim(lLowerArm, lHand, OUT_L * ARM_REST.lower.x, -1, ARM_REST.lower.z + armSwing * 0.6);
  }

  // 右臂：手柄追踪优先，其次挥手 / 举手，最后才是休息姿势
  if (rightTarget && rUpperArm && rLowerArm && rHand) {
    poseArmIK(
      rUpperArm,
      rLowerArm,
      rHand,
      rightTarget,
      opts.reach,
      opts.wristOffset,
      opts.tiltOffset,
      opts.yawOffset,
      'right',
      opts.handOffsets?.right ?? null,
      elbowPoleBody.set(OUT_R * 0.25, -1, 0.55).normalize().applyQuaternion(poseBodyQ),
      opts.elbowFlip,
    );
  } else if (opts.wave > 0) {
    // 右臂举到肩上方，小臂左右摆动（14 rad/s ≈ 每秒 2 次）
    const osc = Math.sin(opts.wave * 14) * 0.45;
    aim(rUpperArm, rLowerArm, OUT_R * 0.5, 0.75, 0.02);
    aim(rLowerArm, rHand, OUT_R * (0.15 + osc), 1, -0.05);

    // 手心朝前：解析地算出绕手臂轴要滚多少度
    if (rLowerArm && rHand && opts.palmLocal) {
      alignPalmToForward(rLowerArm, rHand, opts.palmLocal, root.rotation.y);
    }
  } else if (opts.palm > 0) {
    // 举手击掌：手臂**斜上举**而不是水平前伸。
    // 手心法线永远垂直于手臂；如果手臂几乎平行于"正前方"，把它对齐到正前方会退化成
    // 无解（投影长度≈0），算出来的腕部滚转就是乱的——看起来就是"掌心又反了"。
    aim(rUpperArm, rLowerArm, OUT_R * 0.45, 0.55, -0.55);
    aim(rLowerArm, rHand, OUT_R * 0.3, 0.85, -0.35);
    if (rLowerArm && rHand && opts.palmLocal) {
      alignPalmToForward(rLowerArm, rHand, opts.palmLocal, root.rotation.y);
    }
  } else {
    aim(rUpperArm, rLowerArm, OUT_R * ARM_REST.upper.x, -1, ARM_REST.upper.z - armSwing);
    aim(rLowerArm, rHand, OUT_R * ARM_REST.lower.x, -1, ARM_REST.lower.z - armSwing * 0.6);
  }

  /* ---- 腿 ---- */
  // 膝盖朝前：绕身体朝向把世界 −Z 转过来
  const kneePole = new THREE.Vector3(0, 0, -1).applyQuaternion(poseBodyQ);
  const feetT = opts.feet ?? null;
  // 站姿步态：大腿前后摆，小腿在后摆时折叠（脚跟抬起）
  const stride = opts.moving ? opts.swing * 0.5 : 0;
  const bendL = opts.moving ? Math.max(0, -opts.swing) * 0.7 + 0.03 : 0.03;
  const bendR = opts.moving ? Math.max(0, opts.swing) * 0.7 + 0.03 : 0.03;

  if (feetT?.left && lUpperLeg && lLowerLeg && lFoot) {
    // 有脚部目标：脚钉在甲板上，身体升降由膝盖吸收
    solveLimbIK(lUpperLeg, lLowerLeg, lFoot, feetT.left, kneePole);
  } else if (opts.seated) {
    aim(lUpperLeg, lLowerLeg, OUT_L * 0.08, -0.3, -1);
    aim(lLowerLeg, lFoot, OUT_L * 0.06, -1, 0.25);
  } else {
    aim(lUpperLeg, lLowerLeg, OUT_L * 0.1, -1, -stride);
    aim(lLowerLeg, lFoot, OUT_L * 0.05, -1, bendL);
  }

  if (feetT?.right && rUpperLeg && rLowerLeg && rFoot) {
    solveLimbIK(rUpperLeg, rLowerLeg, rFoot, feetT.right, kneePole);
  } else if (opts.seated) {
    aim(rUpperLeg, rLowerLeg, OUT_R * 0.08, -0.3, -1);
    aim(rLowerLeg, rFoot, OUT_R * 0.06, -1, 0.25);
  } else {
    aim(rUpperLeg, rLowerLeg, OUT_R * 0.1, -1, stride);
    aim(rLowerLeg, rFoot, OUT_R * 0.05, -1, bendR);
  }

  // 脚踝朝向放在最后：它只改脚骨的旋转，不动踝关节位置，所以不会破坏上面的 IK
  poseStanceFeet(lFoot, rFoot, opts.footFrames ?? null, poseBodyQ);
}

export async function loadAvatar(url: string, options: LoadOptions = {}): Promise<AvatarInstance> {
  const targetHeight = options.targetHeight ?? 1.62;

  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser));

  const gltf = await loader.loadAsync(url);
  const vrm = gltf.userData.vrm as VRM | undefined;
  if (!vrm) throw new Error('这个文件里没有 VRM 扩展，请确认是 .vrm 或带 VRMC_vrm 的 glb');

  // 0.x 的朝向修正（1.0 上是 no-op）
  VRMUtils.rotateVRM0(vrm);

  const root = new THREE.Group();
  root.name = 'avatar';

  VRMUtils.removeUnnecessaryVertices(vrm.scene);
  if (typeof VRMUtils.combineSkeletons === 'function') {
    VRMUtils.combineSkeletons(vrm.scene);
  }

  // ---- 判断「上」是哪个轴 ----
  // VRM 规范要求 Y-up，但实际文件经常是 Blender 的 Z-up 直接导出。
  // 不能靠猜：用 humanoid 的 hips→head 方向判断，这是唯一可靠的依据。
  const upAxis = detectUpAxis(vrm);
  const upright = new THREE.Group();
  upright.name = 'upright';
  upright.add(vrm.scene);
  if (upAxis === 'Z') upright.rotation.x = -Math.PI / 2; // +Z → +Y
  root.add(upright);

  // ---- 朝向：统一到 glTF 约定（面朝 -Z）----
  const flipped = detectFacingFlip(vrm);
  const facing = new THREE.Group();
  facing.name = 'facing';
  if (flipped) facing.rotation.y = Math.PI;
  root.updateMatrixWorld(true);
  vrm.scene.removeFromParent();
  facing.add(vrm.scene);
  upright.add(facing);

  // ---- 归一化：身高 → 目标值，脚落 y=0，水平居中 ----
  root.updateMatrixWorld(true);
  let box = new THREE.Box3().setFromObject(root);
  const rawSize = box.getSize(new THREE.Vector3());
  const rawMin = box.min.clone();
  const rawMax = box.max.clone();

  const scale = targetHeight > 0 && rawSize.y > 1e-6 ? targetHeight / rawSize.y : 1;
  root.scale.setScalar(scale);
  root.updateMatrixWorld(true);

  box = new THREE.Box3().setFromObject(root);
  // upright 的父节点带 scale，所以局部位移要除以 scale 才能得到期望的世界位移
  upright.position.set(
    -((box.min.x + box.max.x) / 2) / scale,
    -box.min.y / scale,
    -((box.min.z + box.max.z) / 2) / scale,
  );
  root.updateMatrixWorld(true);

  box = new THREE.Box3().setFromObject(root);
  const finalSize = box.getSize(new THREE.Vector3());

  let triangles = 0;
  let meshes = 0;
  const materials = new Set<THREE.Material>();
  const bones = new Set<THREE.Object3D>();
  vrm.scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh) {
      meshes++;
      const geo = mesh.geometry;
      const index = geo.getIndex();
      const pos = geo.getAttribute('position');
      if (index) triangles += index.count / 3;
      else if (pos) triangles += pos.count / 3;
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => materials.add(m));
      else if (mat) materials.add(mat);
    }
    if ((obj as THREE.Bone).isBone) bones.add(obj);
  });

  // 弹簧骨：头发/裙摆，开了更像活的，但每帧有开销
  const springBoneManager = (vrm as unknown as { springBoneManager?: { reset?: () => void } })
    .springBoneManager;
  if (options.springBones === false && springBoneManager?.reset) {
    // 没法直接卸载，退而求其次：让 vrm.update 不跑弹簧骨（由调用方控制）
  }

  const headBone = vrm.humanoid?.getNormalizedBoneNode('head') ?? null;

  // 头骨相对模型根节点的"绑定朝向"。
  // 直接用相机朝向覆盖头骨世界四元数会把绑定朝向（这里是 180° 翻转）也覆盖掉，
  // 结果就是"头绕脖子反了 180°"。正确做法是在这个基准上叠加头显的旋转。
  /** 头朝向翻转开关 */
  let headFlip = false;
  const headFlipQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);

  let headBindRel: THREE.Quaternion | null = null;
  if (headBone) {
    vrm.scene.updateMatrixWorld(true);
    const rootQ = root.getWorldQuaternion(new THREE.Quaternion());
    const headWorldQ = headBone.getWorldQuaternion(new THREE.Quaternion());
    const headPos = headBone.getWorldPosition(new THREE.Vector3());

    // 1) 原样保留绑定朝向（含滚转），不要用"最小旋转"去凑——那会把头的滚转搞乱，
    //    VR 里看起来就是歪着/诡异。
    headBindRel = rootQ.clone().invert().multiply(headWorldQ);

    // 2) 只处理"脸是反的"这一种情况：用眼睛位置判断脸朝向，
    //    如果和模型正面相反，就绕头自身的 Y 轴转 180°（保住上方向，不搞乱滚转）。
    const eyeBones = (['leftEye', 'rightEye'] as const)
      .map((n) => vrm.humanoid?.getNormalizedBoneNode(n as never) ?? null)
      .filter((b): b is THREE.Object3D => !!b);

    if (eyeBones.length === 2) {
      const mid = new THREE.Vector3();
      for (const b of eyeBones) mid.add(b.getWorldPosition(new THREE.Vector3()));
      mid.divideScalar(eyeBones.length);
      const faceWorld = mid.sub(headPos).normalize();
      const modelFwd = new THREE.Vector3(0, 0, -1).applyQuaternion(rootQ).normalize();
      if (faceWorld.dot(modelFwd) < 0) {
        headBindRel.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));
      }
    }
  }

  // 必须在施加任何姿势之前测：绑定姿势（T-pose）下手心朝下
  vrm.scene.updateMatrixWorld(true);
  const humanoid = vrm.humanoid;
  const rightHandBound = humanoid?.getNormalizedBoneNode('rightHand') ?? null;
  const leftHandBound = humanoid?.getNormalizedBoneNode('leftHand') ?? null;
  const palmLocal: THREE.Vector3 | null = rightHandBound ? detectPalmLocal(rightHandBound) : null;
  const palmLocalLeft: THREE.Vector3 | null = leftHandBound ? detectPalmLocal(leftHandBound) : null;

  /**
   * 取某根骨在**身体空间**（root 局部系）下的朝向。
   *
   * 脚要用它：把「绑定姿势下这只脚相对身体怎么转的」存下来，运行时乘上身体朝向就是脚的目标朝向，
   * 脚掌才平、脚尖才不内八。必须在施加任何姿势之前测——一旦摆过姿势，绑定姿势就没了。
   */
  root.updateWorldMatrix(true, false);
  const boneBodyQ = (name: Parameters<NonNullable<typeof humanoid>['getNormalizedBoneNode']>[0]): THREE.Quaternion | null => {
    const node = humanoid?.getNormalizedBoneNode(name) ?? null;
    if (!node) return null;
    node.updateWorldMatrix(true, false);
    const invBody = root.getWorldQuaternion(new THREE.Quaternion()).invert();
    return invBody.multiply(node.getWorldQuaternion(new THREE.Quaternion())).normalize();
  };

  const bindFootQ: BodySpaceRotations = {
    left: boneBodyQ('leftFoot'),
    right: boneBodyQ('rightFoot'),
  };

  /**
   * 模型自己的踝关节高度（绑定姿势下离脚底多少，模型空间）。
   *
   * 之前写死 `0.08 * 缩放`：这是成年真人的踝高，而这个模型是 Q 版、又会被放大到 1.7 倍，
   * 结果鞋底要么陷进甲板、要么悬空。绑定姿势的包围盒底正好是鞋底，
   * 所以直接量「踝骨离根节点的高度」就是答案。
   */
  const ankleHeight = ((): number => {
    const scaleY = root.scale.y || 1;
    const rootY = root.getWorldPosition(new THREE.Vector3()).y;
    const ys: number[] = [];
    for (const name of ['leftFoot', 'rightFoot'] as const) {
      const node = humanoid?.getNormalizedBoneNode(name) ?? null;
      if (!node) continue;
      node.updateWorldMatrix(true, false);
      ys.push((node.getWorldPosition(new THREE.Vector3()).y - rootY) / scaleY);
    }
    if (ys.length === 0) return 0.08;
    return ys.reduce((a, b) => a + b, 0) / ys.length;
  })();

  // 手柄 → 手骨的校准旋转（每只手一份，从骨骼轴向自己算出来）
  type FingerChain = {
    bones: THREE.Object3D[];
    bind: THREE.Quaternion[];
    /** 每节的弯曲转轴（骨骼局部空间）；最后一节没有子骨，用上一节的轴 */
    axes: THREE.Vector3[];
  };

  const FINGER_NAMES = ['thumb', 'index', 'middle', 'ring', 'little'] as const;
  const FINGER_BONES: Record<(typeof FINGER_NAMES)[number], string[]> = {
    thumb: ['ThumbMetacarpal', 'ThumbProximal', 'ThumbDistal'],
    index: ['IndexProximal', 'IndexIntermediate', 'IndexDistal'],
    middle: ['MiddleProximal', 'MiddleIntermediate', 'MiddleDistal'],
    ring: ['RingProximal', 'RingIntermediate', 'RingDistal'],
    little: ['LittleProximal', 'LittleIntermediate', 'LittleDistal'],
  };

  /**
   * 手指弯曲的转轴不能假设成"骨骼局部 X"——实测不少 VRM 的 X 轴接近骨轴，
   * 绕它转只是**拧**，指尖几乎不动。真正该绕的是关节轴：
   *   axis = (指骨方向) × (手心法线)
   * 在绑定姿势下算出它，再换算到每节骨骼的局部空间存起来。
   */
  const buildFingerChains = (
    side: 'left' | 'right',
    palmAxisLocal: THREE.Vector3 | null,
  ): Record<string, FingerChain> => {
    const prefix = side === 'left' ? 'left' : 'right';
    const chains: Record<string, FingerChain> = {};

    const hand = humanoid?.getNormalizedBoneNode((prefix + 'Hand') as never) ?? null;
    if (!hand || !palmAxisLocal) return chains;
    hand.updateWorldMatrix(true, false);
    const palmWorld = palmAxisLocal
      .clone()
      .applyQuaternion(hand.getWorldQuaternion(new THREE.Quaternion()))
      .normalize();

    for (const finger of FINGER_NAMES) {
      const bones: THREE.Object3D[] = [];
      for (const suffix of FINGER_BONES[finger]) {
        const bone = humanoid?.getNormalizedBoneNode((prefix + suffix) as never) ?? null;
        if (bone) bones.push(bone);
      }
      if (bones.length === 0) continue;

      const axes: THREE.Vector3[] = [];
      for (let i = 0; i < bones.length; i++) {
        const bone = bones[i]!;
        const next = bones[i + 1];
        bone.updateWorldMatrix(true, false);
        if (!next) {
          axes.push(axes[axes.length - 1]?.clone() ?? new THREE.Vector3(0, 0, 1));
          continue;
        }
        next.updateWorldMatrix(true, false);
        const a = bone.getWorldPosition(new THREE.Vector3());
        const b = next.getWorldPosition(new THREE.Vector3());
        const dir = b.sub(a).normalize();
        const axisWorld = new THREE.Vector3().crossVectors(dir, palmWorld).normalize();
        if (axisWorld.lengthSq() < 1e-6) {
          axes.push(new THREE.Vector3(0, 0, 1));
          continue;
        }
        // 世界轴 → 骨骼局部空间
        const boneQ = bone.getWorldQuaternion(new THREE.Quaternion()).invert();
        axes.push(axisWorld.applyQuaternion(boneQ).normalize());
      }

      chains[finger] = { bones, bind: bones.map((b) => b.quaternion.clone()), axes };
    }
    return chains;
  };

  /** 手臂拉伸比例（1 = 原始比例） */
  const armStretch = { left: 1, right: 1 };

  const armLengths = (() => {
    const measureSide = (side: 'left' | 'right'): number => {
      const prefix = side;
      const upper = humanoid?.getNormalizedBoneNode((prefix + 'UpperArm') as never) ?? null;
      const lower = humanoid?.getNormalizedBoneNode((prefix + 'LowerArm') as never) ?? null;
      const hand = humanoid?.getNormalizedBoneNode((prefix + 'Hand') as never) ?? null;
      if (!upper || !lower || !hand) return 0;
      upper.updateWorldMatrix(true, false);
      lower.updateWorldMatrix(true, false);
      hand.updateWorldMatrix(true, false);
      const a = upper.getWorldPosition(new THREE.Vector3());
      const b = lower.getWorldPosition(new THREE.Vector3());
      const c = hand.getWorldPosition(new THREE.Vector3());
      return a.distanceTo(b) + b.distanceTo(c);
    };
    return { left: measureSide('left'), right: measureSide('right') };
  })();

  const applyArmStretch = (side: 'left' | 'right'): void => {
    const prefix = side;
    const upper = humanoid?.getNormalizedBoneNode((prefix + 'UpperArm') as never) ?? null;
    const lower = humanoid?.getNormalizedBoneNode((prefix + 'LowerArm') as never) ?? null;
    const hand = humanoid?.getNormalizedBoneNode((prefix + 'Hand') as never) ?? null;
    const k = armStretch[side];
    if (upper) upper.scale.setScalar(k);
    if (lower) lower.scale.setScalar(k);
    // 手不跟着变大：反向缩放，位置仍由父级决定
    if (hand) hand.scale.setScalar(1 / k);
  };

  const fingerChains = {
    left: buildFingerChains('left', palmLocalLeft),
    right: buildFingerChains('right', palmLocal),
  };

  const fingerCurls: {
    left: FingerCurls | null;
    right: FingerCurls | null;
  } = { left: null, right: null };

  const curlQ = new THREE.Quaternion();
  /** 手指弯曲方向（默认朝手心） */
  let curlFlip = false;
  let elbowFlip = false;
  /** 头部俯仰，分摊到脊柱 */
  let headPitch = 0;

  /** 绕每节算出来的关节轴往手心方向弯 */
  const applyFingerCurls = (side: 'left' | 'right'): void => {
    const curls = fingerCurls[side];
    const chains = fingerChains[side];
    for (const finger of FINGER_NAMES) {
      const chain = chains[finger];
      if (!chain) continue;
      const amount = curls ? curls[finger] * (finger === 'thumb' ? 0.6 : 1) : 0;
      for (let i = 0; i < chain.bones.length; i++) {
        const bone = chain.bones[i]!;
        const bind = chain.bind[i]!;
        if (amount <= 0.002) {
          bone.quaternion.copy(bind);
          continue;
        }
        const limit = finger === 'thumb' ? 0.7 : i === 0 ? 0.9 : 1.2;
        const axis = chain.axes[i] ?? chain.axes[chain.axes.length - 1] ?? new THREE.Vector3(0, 0, 1);
        const signed = curlFlip ? -amount * limit : amount * limit;
        bone.quaternion.copy(bind).multiply(curlQ.setFromAxisAngle(axis, signed));
      }
    }
  };

  const handOffsets = {
    left: null as THREE.Quaternion | null,
    right: rightHandBound ? computeHandOffset(humanoid, rightHandBound, 'right', palmLocal) : null,
  };

  // 左手直接用右手镜像：骨骼轴向在左右手上是镜像的，逐只手检测容易翻车（实测左手指根骨可能缺失），
  // 而 VRM 左右手几乎总是严格镜像，所以反射一次比再测一遍可靠。
  if (handOffsets.right) {
    const mirror = new THREE.Matrix4().makeScale(-1, 1, 1);
    const m = new THREE.Matrix4()
      .makeRotationFromQuaternion(handOffsets.right)
      .premultiply(mirror)
      .multiply(mirror);
    handOffsets.left = new THREE.Quaternion().setFromRotationMatrix(m);
  }

  applyRelaxedPose(vrm, root, bindFootQ);
  vrm.update(0);

  /* ---------------- 表情 / 眨眼 ---------------- */
  const expressionManager = vrm.expressionManager ?? null;
  const expressions = expressionManager
    ? expressionManager.expressions.map((e) => String(e.expressionName))
    : [];

  /**
   * 表情兜底：这个模型的 VRM 里 18 个表情的 morphTargetBinds **全是空的**
   * （导出时没绑），但网格上确实有带名字的 morph target。
   * 所以这里按名字直接驱动 morphTargetInfluences。
   *
   * 有正常绑定的模型不受影响（hasExpressionBinds = true 时走官方路径）。
   */
  const hasExpressionBinds =
    !!expressionManager && expressionManager.expressions.some((e) => (e.binds?.length ?? 0) > 0);

  /** meshName → (morph 名 → influence 下标) */
  const morphIndex = new Map<string, Map<string, number>>();
  vrm.scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.morphTargetDictionary || !mesh.morphTargetInfluences) return;
    const map = new Map<string, number>();
    for (const [name, idx] of Object.entries(mesh.morphTargetDictionary)) map.set(name, idx as number);
    morphIndex.set(mesh.name, map);
  });

  /** VRM 表情名 → [网格名, morph 名]（这个模型的 morph 是日文命名） */
  const MORPH_FALLBACK: Record<string, Array<[string, string]>> = {
    blink: [['eyebrows', 'まばたき']],
    happy: [['eyebrows', '笑い']],
    angry: [['eyebrows', 'じと目']],
    surprised: [
      ['eyes', 'びっくり'],
      ['mouth_inner', 'あ'],
    ],
    relaxed: [['eyebrows', '笑い']],
    aa: [['mouth_inner', 'あ']],
    ih: [['mouth_inner', 'い']],
    ou: [['mouth_inner', 'う']],
    ee: [['mouth_inner', 'え']],
    oh: [['mouth_inner', 'お']],
  };

  /**
   * 每个表情的权重缩放。
   * 这个模型没有"微笑"morph，只有挤眼睛的"笑い"，满权重会挤成一条缝、很诡异，
   * 所以压到 0.4；眨眼同理不要 1.0（会露出内眼白）。
   */
  const EXPRESSION_SCALE: Record<string, number> = {
    happy: 0.4,
    relaxed: 0.4,
    blink: 0.8,
  };

  const applyExpression = (name: string, weight: number): void => {
    const w = Math.max(0, Math.min(1, weight)) * (EXPRESSION_SCALE[name] ?? 1);
    if (hasExpressionBinds) {
      expressionManager?.setValue(name as never, w);
      return;
    }
    const binds = MORPH_FALLBACK[name];
    if (!binds) return;
    for (const [meshName, morphName] of binds) {
      const mesh = vrm.scene.getObjectByName(meshName) as THREE.Mesh | null;
      const idx = morphIndex.get(meshName)?.get(morphName);
      if (!mesh || idx === undefined || !mesh.morphTargetInfluences) continue;
      mesh.morphTargetInfluences[idx] = w;
    }
  };

  /** 正在按时间淡出的表情 */
  const pulses = new Map<string, number>();

  // 眨眼：随机 2.2–5.5 s 一次，眼皮合上再睁开约 130 ms
  let blinkTimer = 2 + Math.random() * 3;
  let blinkPhase = -1; // -1 = 没在眨，0..1 = 进度

  function updateFace(dt: number): void {
    const em = expressionManager;
    if (!em) return;

    // 眨眼
    if (blinkPhase < 0) {
      blinkTimer -= dt;
      if (blinkTimer <= 0) blinkPhase = 0;
    } else {
      blinkPhase += dt / 0.13;
      const w = blinkPhase < 0.5 ? blinkPhase * 2 : (1 - blinkPhase) * 2;
      applyExpression('blink', Math.max(0, Math.min(1, w)));
      if (blinkPhase >= 1) {
        applyExpression('blink', 0);
        blinkPhase = -1;
        blinkTimer = 2.2 + Math.random() * 3.3;
      }
    }

    // 一次性表情：线性淡出
    for (const [name, remaining] of pulses) {
      const next = remaining - dt;
      if (next <= 0) {
        applyExpression(name, 0);
        pulses.delete(name);
      } else {
        pulses.set(name, next);
        applyExpression(name, Math.min(1, next / 0.35));
      }
    }
  }

  const measure: AvatarMeasure = {
    rawHeight: rawSize.y,
    rawMin: [rawMin.x, rawMin.y, rawMin.z],
    rawMax: [rawMax.x, rawMax.y, rawMax.z],
    height: finalSize.y,
    scale,
    ankleHeight: Number.isFinite(ankleHeight) && ankleHeight > 0 ? ankleHeight : 0.08,
    upAxis,
    flipped,
    triangles: Math.round(triangles),
    meshes,
    materials: materials.size,
    bones: bones.size,
  };

  /* 移动状态：由 setPose / setLocomotion 写入，update 里统一摆姿势 */
  let seated = true;
  let walkSpeed = 0;
  let walkPhase = 0;
  /** 挥手的剩余时间（秒）；> 0 表示正在挥 */
  let waveRemaining = 0;
  let waveElapsed = 0;
  /** 举手（击掌）的剩余时间（秒） */
  let palmRemaining = 0;
  let palmElapsed = 0;
  /** 上一次摆姿势时的状态指纹：站定且没转身就不必每帧重算骨骼 */
  let lastPoseKey = '';

  /* 手柄追踪的目标；null 表示这一侧走程序化姿态 */
  let handTargets: { left: HandTarget | null; right: HandTarget | null } | null = null;
  let feetTargets: { left: FootTarget | null; right: FootTarget | null } | null = null;
  let reachScale = 1;
  let wristOffset = 0;
  let tiltOffset = 0;
  let yawOffset = 0;

  return {
    root,
    vrm,
    measure,
    headBone,
    expressions,
    setHandTargets(hands): void {
      // 拷贝一份，避免调用方复用同一批临时对象
      const copy = (h: HandTarget | null): HandTarget | null =>
        h
          ? {
              position: h.position.clone(),
              quaternion: h.quaternion ? h.quaternion.clone() : null,
            }
          : null;
      handTargets = hands ? { left: copy(hands.left), right: copy(hands.right) } : null;
      lastPoseKey = ''; // 立刻重摆
    },
    setFingerCurls(side: 'left' | 'right', curls: FingerCurls | null): void {
      fingerCurls[side] = curls ? { ...curls } : null;
    },
    setHeadFlip(flip: boolean): void {
      headFlip = flip;
    },
    setHeadRotation(cameraWorldQuat: THREE.Quaternion): void {
      const head = vrm.humanoid?.getNormalizedBoneNode('head');
      if (!head || !headBindRel) return;

      // 目标世界朝向 = 相机朝向 × 绑定朝向（保留头骨自身的朝向约定）
      const targetWorld = cameraWorldQuat.clone().multiply(headBindRel);
      if (headFlip) targetWorld.multiply(headFlipQ);

      head.parent?.updateWorldMatrix(true, false);
      const parentQ = head.parent
        ? head.parent.getWorldQuaternion(new THREE.Quaternion()).invert()
        : new THREE.Quaternion();
      head.quaternion.copy(parentQ.multiply(targetWorld));
      head.updateWorldMatrix(false, true);
    },
    setHeadHidden(hidden: boolean): void {
      vrm.scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!mesh.isMesh) return;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        const names = mats.map((m) => (m?.name ?? '').toLowerCase());
        if (names.some((n) => HIDE_IN_FIRST_PERSON.has(n))) mesh.visible = !hidden;
      });
    },
    setFingerCurlFlip(flip: boolean): void {
      curlFlip = flip;
    },
    armLengths,
    setArmStretch(side: 'left' | 'right', scale: number): void {
      const k = Math.max(1, Math.min(1.6, scale));
      if (Math.abs(armStretch[side] - k) < 0.005) return;
      armStretch[side] = k;
      applyArmStretch(side);
      lastPoseKey = '';
    },
    setElbowFlip(flip: boolean): void {
      elbowFlip = flip;
      lastPoseKey = '';
    },
    setHeadPitch(pitch: number): void {
      headPitch = pitch;
      lastPoseKey = '';
    },
    setFootTargets(feet): void {
      const copy = (v: FootTarget | null): FootTarget | null => (v ? v.clone() : null);
      const same = (a: FootTarget | null, b: FootTarget | null): boolean =>
        (a === null && b === null) ||
        (a !== null && b !== null && a.distanceToSquared(b) < 1e-8);
      const next = feet ? { left: copy(feet.left), right: copy(feet.right) } : null;
      // 这个方法每帧都会被调用（VR 里脚的目标随身体移动），
      // 只有真的变了才让姿势失效；否则"指纹跳过重算"就被它整个废掉了。
      if (
        same(next?.left ?? null, feetTargets?.left ?? null) &&
        same(next?.right ?? null, feetTargets?.right ?? null)
      ) {
        return;
      }
      feetTargets = next;
      lastPoseKey = '';
    },
    setReachScale(next: number): void {
      reachScale = Math.max(0.5, Math.min(1.5, next));
      lastPoseKey = '';
    },
    setWristOffset(roll: number, tilt: number, yaw: number): void {
      wristOffset = roll;
      tiltOffset = tilt;
      yawOffset = yaw;
      lastPoseKey = '';
    },
    handTransform(side: 'left' | 'right'): { position: THREE.Vector3; quaternion: THREE.Quaternion } | null {
      const bone = vrm.humanoid?.getNormalizedBoneNode(
        (side === 'left' ? 'leftHand' : 'rightHand') as never,
      );
      if (!bone) return null;
      bone.updateWorldMatrix(true, false);
      return {
        position: bone.getWorldPosition(new THREE.Vector3()),
        quaternion: bone.getWorldQuaternion(new THREE.Quaternion()),
      };
    },
    setPose(next: boolean): void {
      seated = next;
    },
    setLocomotion(speed: number): void {
      walkSpeed = Math.max(0, speed);
    },
    pulseGesture(name: string, duration?: number): void {
      if (name === 'wave') {
        waveRemaining = duration ?? 1.6;
        waveElapsed = 0;
      } else if (name === 'palm') {
        palmRemaining = duration ?? 2.6;
        palmElapsed = 0;
      } else {
        return;
      }
      lastPoseKey = ''; // 强制下一帧重摆
    },
    handPosition(out?: THREE.Vector3): THREE.Vector3 | null {
      const hand = vrm.humanoid?.getNormalizedBoneNode('rightHand');
      if (!hand) return null;
      const target = out ?? new THREE.Vector3();
      return hand.getWorldPosition(target);
    },
    palmNormalWorld(): THREE.Vector3 | null {
      const hand = vrm.humanoid?.getNormalizedBoneNode('rightHand');
      if (!hand || !palmLocal) return null;
      hand.updateWorldMatrix(true, false);
      return palmLocal.clone().applyQuaternion(hand.getWorldQuaternion(new THREE.Quaternion()));
    },
    pulseExpression(name: string, duration = 1.4): void {
      if (!expressionManager) return;
      if (!expressions.includes(name)) return;
      pulses.set(name, duration);
      applyExpression(name, 1);
    },
    setExpression(name: string, weight: number): void {
      if (!expressionManager || !expressions.includes(name)) return;
      applyExpression(name, weight);
    },
    update(dt: number): void {
      const moving = !seated && walkSpeed > 0.05;
      // 步频：1.35 m/s 时约 1 Hz 一个完整步态周期（≈ 每秒 2 步）
      if (moving) walkPhase += Math.min(walkSpeed, 2) * 4.6 * dt;

      if (waveRemaining > 0) {
        waveRemaining -= dt;
        waveElapsed += dt;
        if (waveRemaining <= 0) lastPoseKey = '';
      }
      if (palmRemaining > 0) {
        palmRemaining -= dt;
        palmElapsed += dt;
        if (palmRemaining <= 0) lastPoseKey = '';
      }

      // 摆姿势要算一串世界矩阵（每根骨都要刷新子树），站定 + 没转身时结果和上一帧一样，
      // 所以用指纹跳过——坐着的聊天场景里这能省掉几乎全部开销。
      // 手柄追踪时手一直在动，指纹里带上位置（量化到 1 cm）。
      const tracked = handTargets && (handTargets.left || handTargets.right) ? handTargets : null;
      const q = (h: HandTarget | null): string =>
        h
          ? `${h.position.x.toFixed(2)},${h.position.y.toFixed(2)},${h.position.z.toFixed(2)}|` +
            (h.quaternion
              ? `${h.quaternion.x.toFixed(2)},${h.quaternion.y.toFixed(2)},${h.quaternion.z.toFixed(2)},${h.quaternion.w.toFixed(2)}`
              : '-')
          : '-';
      // 脚的指纹：VR 里脚被钉在甲板上，身体挪动（低头/下蹲/走路）时它才变，
      // 不带上的话"人动了脚不动"或者"脚动了 pose 不重算"。
      const fq = (v: FootTarget | null): string =>
        v ? `${v.x.toFixed(2)},${v.y.toFixed(2)},${v.z.toFixed(2)}` : '-';
      const feetKey = `${fq(feetTargets?.left ?? null)}|${fq(feetTargets?.right ?? null)}`;

      const key = tracked
        ? `k|${seated}|${root.rotation.y.toFixed(2)}|${headPitch.toFixed(2)}|${feetKey}|${q(tracked.left)}|${q(tracked.right)}`
        : palmRemaining > 0
          ? `p|${Math.round(palmElapsed * 30)}|${seated}|${root.rotation.y.toFixed(2)}`
          : waveRemaining > 0
            ? `w|${Math.round(waveElapsed * 30)}|${seated}|${root.rotation.y.toFixed(2)}`
            : moving
              ? `m|${seated}|${Math.round(walkPhase * 20)}|${root.rotation.y.toFixed(2)}`
              : `s|${seated}|${root.rotation.y.toFixed(3)}|${headPitch.toFixed(2)}|${feetKey}`;
      if (key !== lastPoseKey) {
        lastPoseKey = key;
        poseBody(vrm, root, {
          seated,
          moving,
          swing: Math.sin(walkPhase),
          wave: waveRemaining > 0 ? waveElapsed : 0,
          palm: palmRemaining > 0 ? palmElapsed : 0,
          palmLocal,
          hands: handTargets,
          feet: feetTargets,
          reach: reachScale,
          wristOffset,
          tiltOffset,
          headPitch,
          yawOffset,
          elbowFlip,
          handOffsets,
          footFrames: bindFootQ,
        });
      }

      // 手指在手柄按键后才弯，没按键时恢复绑定姿势
      applyFingerCurls('left');
      applyFingerCurls('right');

      updateFace(dt);
      vrm.update(dt);
    },
    dispose(): void {
      VRMUtils.deepDispose?.(vrm.scene);
    },
  };
}
